import { useEffect, useRef } from 'react';
import { Form, InputNumber, Radio, Tag } from 'antd';
import type { FormInstance } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { NO_CANCEL_REASON_LABEL, type CancelReasonPayRule } from '@salaries/shared';
import { getCancelReasons } from '../../api/cancelReasons';
import { Loader } from '../../components/Loader';

// Cancelled-class pay: a class the box cancelled pays a PERCENTAGE of its fee,
// and the percentage depends on WHY it was cancelled. The switch + the catch-all
// percentage are ordinary form fields (saved with the rest of the salary
// defaults); the per-reason percentages are a list, held here in the parent's
// state and saved through their own endpoint.
//
// Arbox has no endpoint listing a box's cancellation reasons — the list comes
// from the reasons that actually appeared in the box's own recent classes.

export function CancelledClassPayFields({
  form,
  open,
  rules,
  onRulesChange,
}: {
  form: FormInstance;
  /** Only fetch the reason list once the card is opened — it costs a report call. */
  open: boolean;
  /** null until the reason list has loaded — the parent uses that to know the
   *  rules were never seen this session, and so must not be overwritten on save. */
  rules: CancelReasonPayRule[] | null;
  onRulesChange: (rules: CancelReasonPayRule[]) => void;
}) {
  const enabled = Form.useWatch('cancelled_class_pay_enabled', form);
  const reasonsQuery = useQuery({
    queryKey: ['cancel-reasons'],
    queryFn: getCancelReasons,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // The catalog carries each reason's stored %, so it doubles as the initial
  // value of the rules the parent will save. Seed once — later edits are the
  // operator's and must not be clobbered by a refetch.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !reasonsQuery.data) return;
    seeded.current = true;
    onRulesChange(
      reasonsQuery.data
        .filter((r) => r.percent > 0)
        .map((r) => ({
          cancel_reason_id: r.cancel_reason_id,
          cancel_reason_name: r.cancel_reason_name,
          percent: r.percent,
        })),
    );
  }, [reasonsQuery.data, onRulesChange]);

  const percentOf = (id: number) =>
    (rules ?? []).find((r) => r.cancel_reason_id === id)?.percent ?? 0;

  const setPercent = (id: number, name: string | null, percent: number) => {
    const rest = (rules ?? []).filter((r) => r.cancel_reason_id !== id);
    onRulesChange(
      percent > 0
        ? [...rest, { cancel_reason_id: id, cancel_reason_name: name, percent }]
        : rest,
    );
  };

  const reasons = reasonsQuery.data ?? [];

  return (
    <Form.Item name="cancelled_class_pay_enabled" className="mb-0">
      <Radio.Group className="flex flex-col gap-3">
        <Radio value={false}>ללא תשלום על שיעורים שבוטלו</Radio>
        <Radio value={true}>תשלום לפי סיבת הביטול</Radio>
        <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
          <div className="text-small text-secondary-text mb-2">
            אחוז מהשכר שהיה משולם עבור השיעור. ברירת המחדל היא 0% - כלומר שיעור שבוטל לא יוסיף
            תשלום נוסף.
          </div>

          <Form.Item
            name="cancelled_class_default_percent"
            label={`כל שאר הביטולים (כולל ${NO_CANCEL_REASON_LABEL})`}
            className="mb-3"
          >
            <InputNumber min={0} max={100} dir="ltr" style={{ width: 120 }} addonAfter="%" />
          </Form.Item>

          {reasonsQuery.isLoading ? (
            <Loader />
          ) : reasonsQuery.isError ? (
            <div className="text-small text-error">שגיאה בטעינת סיבות הביטול</div>
          ) : reasons.length === 0 ? (
            <div className="text-small text-secondary-text">
              לא נמצאו ביטולים עם סיבה בשלושת החודשים האחרונים. כל ביטול ישולם לפי האחוז שלמעלה
              בלבד.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {reasons.map((r) => (
                <div key={r.cancel_reason_id} className="flex items-center gap-2">
                  <span className="min-w-40">{r.cancel_reason_name || NO_CANCEL_REASON_LABEL}</span>
                  {r.recent_count > 0 && (
                    <Tag color="default" className="m-0">
                      {r.recent_count}
                    </Tag>
                  )}
                  <span className="flex-1" />
                  <InputNumber
                    min={0}
                    max={100}
                    dir="ltr"
                    style={{ width: 120 }}
                    addonAfter="%"
                    value={percentOf(r.cancel_reason_id)}
                    onChange={(v) =>
                      setPercent(r.cancel_reason_id, r.cancel_reason_name, Number(v) || 0)
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Radio.Group>
    </Form.Item>
  );
}
